import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';

import { LoginComponent } from './login/login.component';
import { ChangePasswordComponent } from './change-password/change-password.component';
import { passwordChangeGuard } from './guards/password-change.guard';
import { PortalLayoutComponent } from './portal-layout/portal-layout.component';

import { AuthGuard } from './guards/auth.guard';
import { roleGuard } from './guards/role.guard';
import { rmmMfaGuard } from './guards/rmm-mfa.guard';

const administrationRoles = ['admin', 'technician', 'viewer', 'api'];
const internalRoles = ['admin', 'technician', 'api'];
const rmmRoles = ['admin', 'api'];

const routes: Routes = [
  { path: 'login-portal', component: LoginComponent },
  { path: 'cambiar-contrasena', component: ChangePasswordComponent, canActivate: [passwordChangeGuard] },
  {
    path: '',
    component: PortalLayoutComponent,
    canActivate: [AuthGuard],
    children: [
      {
        path: 'farmacias',
        loadChildren: () => import('./farmacias/farmacias.module').then(m => m.FarmaciasModule),
        canActivate: [roleGuard],
        data: { roles: administrationRoles }
      },
      {
        path: 'fedicom',
        loadChildren: () => import('./fedicom/fedicom.module').then(m => m.FedicomModule),
        canActivate: [roleGuard],
        data: { roles: administrationRoles }
      },
      {
        path: 'fedicom-descarga',
        loadChildren: () => import('./fedicom-descarga/fedicom-descarga.module').then(m => m.FedicomDescargaModule),
        canActivate: [roleGuard],
        data: { roles: administrationRoles }
      },
      {
        path: 'paquetes',
        loadChildren: () => import('./paquetes/paquetes.module').then(m => m.PaquetesModule),
        canActivate: [roleGuard],
        data: { roles: administrationRoles }
      },
      {
        path: 'grupos',
        loadChildren: () => import('./grupos/grupos.module').then(m => m.GruposModule),
        canActivate: [roleGuard],
        data: { roles: administrationRoles }
      },
      {
        path: 'mapa',
        loadChildren: () => import('./mapa/mapa.module').then(m => m.MapaModule),
        canActivate: [roleGuard],
        data: { roles: administrationRoles }
      },
      {
        path: 'control-uso',
        loadChildren: () => import('./control-uso/control-uso.module').then(m => m.ControlUsoModule),
        canActivate: [roleGuard],
        data: { roles: administrationRoles }
      },
      {
        path: 'farmaia',
        redirectTo: 'hermes',
        pathMatch: 'prefix'
      },
      {
        path: 'whatsapp',
        loadChildren: () => import('./whatsapp/whatsapp.module').then(m => m.WhatsappFeatureModule),
        canActivate: [roleGuard],
        data: { roles: administrationRoles }
      },
      {
        path: 'email/mailrelay-usage',
        loadChildren: () => import('./mailrelay-usage/mailrelay-usage.module').then(m => m.MailrelayUsageModule),
        canActivate: [roleGuard],
        data: {
          roles: internalRoles,
          title: 'MailRelay',
          subtitle: 'Control de consumo de envios, limites, suscriptores y plan contratado.'
        }
      },
      {
        path: 'email',
        loadChildren: () => import('./email-reporting/email-reporting.module').then(m => m.EmailReportingModule),
        canActivate: [roleGuard],
        data: {
          roles: internalRoles,
          title: 'Email',
          subtitle: 'Reporting de envios, entregabilidad, visualizaciones, clicks e interaccion por cliente.'
        }
      },
      {
        path: 'ordenes',
        loadChildren: () => import('./ordenes/ordenes.module').then(m => m.OrdenesModule),
        canActivate: [roleGuard],
        data: { roles: internalRoles }
      },
      {
        path: 'ordenes-sb',
        loadChildren: () => import('./ordenes-sb/ordenes-sb.module').then(m => m.OrdenesSbModule),
        canActivate: [roleGuard],
        data: { roles: internalRoles }
      },
      {
        path: 'estado',
        loadChildren: () => import('./estado/estado.module').then(m => m.EstadoModule),
        canActivate: [roleGuard],
        data: { roles: internalRoles }
      },
      {
        path: 'control-cargas',
        loadChildren: () => import('./control-cargas/control-cargas.module').then(m => m.ControlCargasModule),
        canActivate: [roleGuard],
        data: { roles: internalRoles }
      },
      {
        path: 'sentinel',
        loadChildren: () => import('./cargas-diarias/cargas-diarias.module').then(m => m.CargasDiariasModule),
        canActivate: [roleGuard],
        data: { roles: internalRoles, title: 'Sentinel - DataOps' }
      },
      {
        path: 'cargas-diarias',
        redirectTo: 'sentinel',
        pathMatch: 'full'
      },
      {
        path: 'argos',
        loadChildren: () => import('./datagraph/datagraph.module').then(m => m.DatagraphModule),
        canActivate: [roleGuard],
        data: { roles: internalRoles, title: 'Argos - DataGraph' }
      },
      {
        path: 'datagraph',
        redirectTo: 'argos',
        pathMatch: 'full'
      },
      {
        path: 'hermes',
        loadChildren: () => import('./hermes/hermes.module').then(m => m.HermesModule),
        canActivate: [roleGuard],
        data: { roles: internalRoles, title: 'Hermes - FarmaIA' }
      },
      {
        path: 'control-calidad',
        loadChildren: () => import('./control-calidad/control-calidad.module').then(m => m.ControlCalidadModule),
        canActivate: [roleGuard],
        data: {
          roles: internalRoles,
          title: 'Control de calidad',
          subtitle: 'Monitor de incidencias de calidad por farmacia para la ultima ejecucion disponible.'
        }
      },
      {
        path: 'control-cargas-bbdd',
        loadChildren: () => import('./control-cargas-bbdd/control-cargas-bbdd.module').then(m => m.ControlCargasBbddModule),
        canActivate: [roleGuard],
        data: { roles: internalRoles }
      },
      {
        path: 'programacion-tareas',
        loadChildren: () => import('./programacion-tareas/programacion-tareas.module').then(m => m.ProgramacionTareasModule),
        canActivate: [roleGuard],
        data: { roles: internalRoles }
      },
      {
        path: 'programacion-tareas-v2',
        loadChildren: () => import('./programacion-tareas-v2/programacion-tareas-v2.module').then(m => m.ProgramacionTareasV2Module),
        canActivate: [roleGuard],
        data: { roles: internalRoles }
      },
      {
        path: 'fingerprint',
        loadChildren: () => import('./fingerprint/fingerprint.module').then(m => m.FingerprintModule),
        canActivate: [roleGuard],
        data: { roles: internalRoles }
      },
      {
        path: 'heartbeat',
        loadChildren: () => import('./heartbeat/heartbeat.module').then(m => m.HeartbeatModule),
        canActivate: [roleGuard],
        data: { roles: internalRoles }
      },
      {
        path: 'anydesk',
        loadChildren: () => import('./anydesk/anydesk.module').then(m => m.AnyDeskModule),
        canActivate: [roleGuard],
        data: { roles: internalRoles }
      },
      {
        path: 'usuarios', loadChildren: () => import('./usuarios/usuarios.module').then(m => m.UsuariosModule), canActivate: [roleGuard], data: { roles: administrationRoles }
      },
      {
        path: 'rmm',
        loadChildren: () => import('./rmm/rmm.module').then(m => m.RmmModule),
        canActivate: [roleGuard, rmmMfaGuard],
        data: { roles: rmmRoles }
      },
      {
        path: 'azure-key-vault',
        loadChildren: () => import('./key-vault/key-vault.module').then(m => m.KeyVaultModule),
        data: { title: 'Azure Key Vault' }
      },
      {
        path: 'logs-rmm',
        loadChildren: () => import('./logs/logs.module').then(m => m.LogsModule),
        canActivate: [roleGuard, rmmMfaGuard],
        data: { roles: rmmRoles, rmmLogs: true }
      },
      {
        path: 'calendario-motor',
        loadChildren: () => import('./calendario-motor/calendario-motor.module').then(m => m.CalendarioMotorModule),
        canActivate: [roleGuard, rmmMfaGuard], data: { roles: rmmRoles }
      },
      {
        path: 'tareas-motor',
        loadChildren: () => import('./tareas-motor/tareas-motor.module').then(m => m.TareasMotorModule),
        canActivate: [roleGuard, rmmMfaGuard],
        data: { roles: rmmRoles }
      },
      {
        path: 'auditoria', redirectTo: 'logs-rmm?view=auditoria', pathMatch: 'full'
      },
      {
        path: 'rmm-verificar',
        loadComponent: () => import('./rmm-mfa/rmm-mfa.component').then(m => m.RmmMfaComponent),
        canActivate: [roleGuard],
        data: { roles: rmmRoles }
      },
      {
        path: 'vacaciones',
        loadChildren: () => import('./vacaciones/vacaciones.module').then(m => m.VacacionesModule),
        canActivate: [roleGuard],
        data: { roles: internalRoles }
      },
      {
        path: 'errores',
        loadChildren: () => import('./errores/errores.module').then(m => m.ErroresModule),
        canActivate: [roleGuard],
        data: { roles: internalRoles }
      },
      {
        path: 'polling',
        loadChildren: () => import('./polling/polling.module').then(m => m.PollingModule),
        canActivate: [roleGuard],
        data: { roles: internalRoles }
      },
      {
        path: 'logs',
        loadChildren: () => import('./logs/logs.module').then(m => m.LogsModule),
        canActivate: [roleGuard],
        data: { roles: internalRoles }
      },
      {
        path: 'control-cargas-parciales',
        loadChildren: () => import('./control-cargas-parciales/control-cargas-parciales.module').then(m => m.ControlCargasParcialesModule),
        canActivate: [roleGuard],
        data: { roles: internalRoles }
      },
      { path: '', redirectTo: 'farmacias', pathMatch: 'full' }
    ]
  },
  { path: '**', redirectTo: 'login-portal' }
];

@NgModule({
  imports: [RouterModule.forRoot(routes)],
  exports: [RouterModule]
})
export class AppRoutingModule {}
